// apps/account-portal/src/pages/ServiceCategoryPage.tsx
import React, { useEffect, useContext } from 'react';
import { UserContext } from '../context/UserContext';
import SystemCard from '../components/SystemCard';
import { Alert } from '@redpanda/ui';
import { useTranslation } from 'react-i18next';
import { SystemType } from '@redpanda/types';
import { useSubscriptionStore } from '../stores/subscriptionStore';
import PageFooter from '../components/PageFooter';
import PageContainer from '../components/PageContainer';
import Seo from '../components/Seo';
import LoginPromptBanner from '../components/LoginPromptBanner';
import { ENTERPRISE_SYSTEM_CONFIGS, STORE_SYSTEM_CONFIGS } from '../constants/systemDefinitions';

const SEO_CONTENT: Record<'enterprise' | 'store', { title: string; description: string }> = {
  enterprise: {
    title: '企業服務方案 | RedPanda Tech 小熊貓科技',
    description: '提供中小企業雲端 CRM、會計系統、專案管理等企業數位化服務方案，協助企業提升營運效率。',
  },
  store: {
    title: '店家服務方案 | RedPanda Tech 小熊貓科技',
    description: '提供店家預約系統、排隊系統等數位化服務方案，協助店家提升營運效率與顧客體驗。',
  },
};

interface ServiceCategoryPageProps {
  category: 'enterprise' | 'store';
}

const ServiceCategoryPage: React.FC<ServiceCategoryPageProps> = ({ category }) => {
  const userContext = useContext(UserContext);
  const { t } = useTranslation();

  const subscriptionStore = useSubscriptionStore();
  const {
    subscriptions,
    subscriptionsError: error,
    fetchAllSubscriptions
  } = subscriptionStore;

  // 從共用常數產生含翻譯文字的系統列表
  const toSystemList = (configs: typeof ENTERPRISE_SYSTEM_CONFIGS) =>
    configs.map(({ system, icon, featureKeys }) => ({
      system,
      icon,
      features: featureKeys.map(key => t(`systems.${system.toLowerCase()}.features.${key}`)),
    }));

  const enterpriseSystems = toSystemList(ENTERPRISE_SYSTEM_CONFIGS);
  const storeSystems = toSystemList(STORE_SYSTEM_CONFIGS);

  useEffect(() => {
    if (userContext?.isAuthenticated) {
      fetchAllSubscriptions();
    }
  }, [userContext?.isAuthenticated, fetchAllSubscriptions]);

  const getSubscription = (system: SystemType) => subscriptions.find(sub => sub.system === system);

  const currentSystems = category === 'enterprise' ? enterpriseSystems : storeSystems;
  const title = category === 'enterprise' ? t('navbar.enterprise') : t('navbar.store');

  return (
    <PageContainer>
      <Seo
        title={SEO_CONTENT[category].title}
        description={SEO_CONTENT[category].description}
        path={`/${category}`}
      />
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-6">
        <h1 className="text-2xl font-semibold text-[var(--text-primary)]">
          {title}
        </h1>
        {!userContext?.isAuthenticated && <LoginPromptBanner />}
      </div>

      {error && (
        <Alert variant="error" title={t('common.status.error')} className="mb-6">
          {error}
        </Alert>
      )}

      <div className="grid gap-5" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
        {currentSystems.map(({ system, icon, features }) => {
          const subscription = getSubscription(system);
          return (
            <SystemCard
              key={system}
              system={system}
              isSubscribed={!!subscription}
              subscriptionStatus={subscription?.status}
              subscriptionEndDate={subscription ? new Date(subscription.endDate) : undefined}
              planName={subscription?.plan?.name}
              planId={subscription?.plan?.id}
              icon={icon}
              features={features}
            />
          );
        })}
      </div>

      <PageFooter />
    </PageContainer>
  );
};

export default ServiceCategoryPage;
